import { deriveIntentForPlanningReport } from '../../../lib/generationImpact/selectedAssessmentIntent';
/**
 * Contextual Key Findings — categorized findings with validation warnings.
 */
import { formatFindingTitle } from '../../../lib/findings/laneItemStructure';
import React from 'react';
import { ReportSourceNote } from './ReportSourceNote';
import { stripAppOnboardingCopy } from '../../../lib/safeNarrativeProse';
import type { ContextualFindingCategory, ContextualKeyFinding, ProjectIntent, SiteRagReport } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import { walkClaimsText } from '../../../lib/walkClaimsText';
import { legacyFindingsToContextual } from '../../../lib/enrichContextualFindings';
import { fingerprintFinding } from '../../../lib/findingFingerprint';
import { deriveKeyFindingsFromClaims } from '../../../lib/keyFindingsDerive';
import { deriveFindingMeta, type FindingMeta } from '../../../lib/keyFindingsPresentation';
import { humanizeIntentSegments } from '../../../lib/intentContextFormat';
import { hasHaleuIntentConflict } from '../../../lib/nuclearFindingRelevance';
import { roleColor } from '../../../lib/reportRoleColors';
import { reportTypographySizes } from '../../../lib/reportSectionHeading';
import { layoutGroupProps } from '../../../lib/reportRendering/layoutGroups';
import { EditorialCallout } from './editorial';

const CATEGORY_LABELS: Record<ContextualFindingCategory, string> = {
  intentAlignment: 'Intent alignment',
  projectDetails: 'Project & site details',
  outcomeInfluence: 'Outcome influence',
};

const CATEGORY_ORDER: ContextualFindingCategory[] = [
  'intentAlignment',
  'projectDetails',
  'outcomeInfluence',
];

const MONO = 'var(--report-body-font, inherit)';

// William QA 2026-06-28 — left accent rule removed report-wide (IMPACT_RULE_ROLE dropped)

/**
 * Normalize finding text for the title/detail duplicate guard: trim,
 * collapse internal whitespace, drop a trailing period, lowercase. When a
 * finding's `detail` reduces to the SAME text as its `title` (a data
 * condition some upstream findings exhibit — e.g. a VERIFIED claim whose
 * title and detail are identical), we render the title only and skip the
 * verbatim echo paragraph. A `detail` that genuinely EXTENDS the title
 * (e.g. title "Substation Z is 1" vs detail "Substation Z is 1.2 mi from
 * the parcel") normalizes differently, so both still render — no
 * information loss.
 */
function normalizeFindingText(s: string | undefined | null): string {
  return (s ?? '').trim().replace(/\s+/g, ' ').replace(/\.\s*$/, '').toLowerCase();
}

/**
 * Bug-fix Wave A — single-citation guard.
 *
 * A derived finding's `source` is a compact label like "Ref 1" while its
 * title/detail prose already carries the SAME citation as an inline
 * `[Ref 1]` token (rendered by `walkClaimsText` as a clickable RefLink).
 * Showing both the inline `[Ref 1]` AND a "Source: Ref 1" footer is two
 * affordances for one source. When the footer's `source` is a `Ref N`
 * label whose N already appears as an inline `[Ref N]` token in the
 * rendered title or detail, the footer is pure duplication and is
 * suppressed — the inline RefLink is the single canonical affordance.
 *
 * Non-ref sources (e.g. "Doc abc", "Climate", "derived") are NOT inline
 * citations, so the footer is the ONLY affordance and is always kept.
 */
function sourceRefNumber(source: string | undefined | null): number | null {
  if (!source) return null;
  const m = /^Ref\s+(\d+)$/i.exec(source.trim());
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

function textHasInlineRef(text: string | undefined | null, n: number): boolean {
  if (!text) return false;
  // Matches the singular `[Ref N]` and either compound form:
  //   `[Ref 1, Ref 2]` (each number prefixed with Ref) or `[Ref 1, 2]`.
  // We scan every `[Ref …]` bracket group and check its member numbers.
  const groups = text.matchAll(/\[Ref\s+[\d\s,]*(?:Ref\s+[\d\s,]*)*\]/gi);
  for (const g of groups) {
    const nums = [...g[0].matchAll(/\d+/g)].map(m => Number(m[0]));
    if (nums.includes(n)) return true;
  }
  return false;
}

/**
 * True when the finding's `source` footer would duplicate a citation that
 * is already shown inline in the title or detail prose.
 */
export function findingSourceIsRedundant(f: {
  title?: string;
  detail?: string;
  source?: string;
}): boolean {
  const n = sourceRefNumber(f.source);
  if (n == null) return false;
  return textHasInlineRef(f.title, n) || textHasInlineRef(f.detail, n);
}

export interface ContextualKeyFindingsBlockProps {
  report: SiteRagReport;
  /**
   * Active report theme. OPTIONAL because the two parents
   * (ProjectIntentOverviewSection + FeasibilityProfileSection) do not yet
   * thread it; `roleColor`/`reportTypographySizes` are null-safe and fall
   * through to DEFAULT_THEME when omitted, so the block stays consistent
   * with no cross-file call-site change required.
   */
  theme?: ReportTheme;
  /** When true, omit the outer "Key Findings" sub-heading (parent supplies it). */
  omitHeading?: boolean;
  className?: string;
  /**
   * DEFECT-A — when true the block renders as a NORMAL fragmentable block,
   * dropping its default `column-span:all` + `column-count:1` exhibit-pin.
   * That pin keeps the block atomic + full-width inside the editorial 2-col
   * deck (the project-intent / exec-summary residual it was added for), but in
   * a single-column MULTICOL host section (the FeasibilityProfileSection radar
   * tail, where `.template-export-cols-2 [data-section-columns="1"]` makes the
   * section a 1-col multicol) the `column-span:all` becomes an un-fragmentable
   * spanner that freezes the whole section onto one page. Hosts that flow this
   * block in a single page column pass `flowInColumn` so it fragments instead.
   * Default false ⇒ byte-stable for the existing callers.
   */
  flowInColumn?: boolean;
}

/**
 * RRQ REPETITION FIX — drop card-vs-card duplicate findings (e.g. two
 * near-identical "Interconnection queue" findings the model emitted). Keys on
 * the coarse `fingerprintFinding(title + detail)` semantic key (lowercase →
 * stopword-strip → sorted significant tokens) so reworded restatements of the
 * same finding collapse to ONE key while genuinely distinct findings don't.
 * Order-stable: the FIRST occurrence wins. (The pre-existing
 * `normalizeFindingText` guard only deduped a card's OWN title vs detail — not
 * card-vs-card; this closes that gap at the render layer so both the on-screen
 * report and the PDF/export inherit it.)
 */
function dedupeFindings(findings: ContextualKeyFinding[]): ContextualKeyFinding[] {
  const seenFull = new Set<string>();
  const seenTitle = new Set<string>();
  const out: ContextualKeyFinding[] = [];
  for (const f of findings) {
    // Full key (title + detail) collapses reworded restatements of the SAME
    // finding; the title-only key additionally collapses two findings that
    // share a title but differ only in a near-identical detail (the observed
    // "Interconnection queue" duplicate — "24-48 months" vs "2-3 years" — same
    // finding stated twice). First occurrence wins, order-stable.
    const fullKey = fingerprintFinding(`${f.title} ${f.detail}`, { category: f.category });
    const titleKey = fingerprintFinding(f.title, { category: f.category });
    if (seenFull.has(fullKey) || seenTitle.has(titleKey)) continue;
    seenFull.add(fullKey);
    seenTitle.add(titleKey);
    out.push(f);
  }
  return out;
}

function resolveFindings(report: SiteRagReport): ContextualKeyFinding[] {
  if (report.contextualKeyFindings && report.contextualKeyFindings.length > 0) {
    return dedupeFindings(report.contextualKeyFindings);
  }
  const direct = report.structuredFindings ?? [];
  const legacy = direct.length > 0 ? direct : deriveKeyFindingsFromClaims(report);
  if (legacy.length === 0) return [];
  return dedupeFindings(legacyFindingsToContextual(legacy, deriveIntentForPlanningReport(report, report.projectIntentAtGen)));
}

/** Split intent summary prose into HUMANIZED chip segments (INTENT-HUMANIZE-1 —
 *  drops "OR:/AND:" editor prefixes + repeated utility clause, ISO dates →
 *  "Dec 2027"), when joined with middle dots. */
function parseIntentContextText(raw: string, intent?: ProjectIntent | null): { chips: string[]; isChipList: boolean } {
  const segments = humanizeIntentSegments(raw, intent);
  if (segments.length > 1) {
    return { chips: segments, isChipList: true };
  }
  return { chips: [segments[0] ?? raw.trim()], isChipList: false };
}

function MetaRow({ label, children, theme }: { label: string; children: React.ReactNode; theme?: ReportTheme }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[6.75rem_minmax(0,1fr)] sm:gap-x-4 sm:items-start">
      <dt
        className="text-[10px] font-bold uppercase tracking-wider leading-snug"
        style={{ color: roleColor(theme, 'muted') }}
      >
        {label}
      </dt>
      <dd className="m-0 min-w-0 text-xs leading-relaxed" style={{ color: roleColor(theme, 'body') }}>{children}</dd>
    </div>
  );
}

function IntentContextValue({ text, theme, intent }: { text: string; theme?: ReportTheme; intent?: ProjectIntent | null }) {
  const { chips, isChipList } = parseIntentContextText(text, intent);
  if (isChipList) {
    return (
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 list-none p-0 m-0" aria-label="Project intent context">
        {chips.map((chip, idx) => (
          <li
            key={chip}
            // BUG #8 (William) — REMOVE the chip box. The intent values render as
            // plain inline text on the cream page (no `border`, no fill, no chip
            // padding). A thin middle-dot separator between values preserves the
            // visual grouping that the chip outlines used to provide.
            className="inline-flex items-baseline text-[11px] font-medium leading-snug"
            style={{ color: roleColor(theme, 'ink') }}
          >
            {idx > 0 && (
              <span aria-hidden="true" className="mr-3" style={{ color: roleColor(theme, 'muted') }}>
                ·
              </span>
            )}
            {chip}
          </li>
        ))}
      </ul>
    );
  }
  return <span>{text}</span>;
}

export const ContextualKeyFindingsBlock: React.FC<ContextualKeyFindingsBlockProps> = ({
  report,
  theme,
  omitHeading = false,
  className = '',
  flowInColumn = false,
}) => {
  const findings = resolveFindings(report);
  if (findings.length === 0) return null;
  const displayIntent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);

  // CTA-SCRUB-2 (2026-07-03) — legacy-persisted reports carry the app
  // onboarding fallback ('Set Project Intent to see…') stamped into every
  // finding's intentContext (the agent path generated intentless until
  // INTENT-THREAD-1). Scrub it at render so the Intent-Context rows OMIT
  // rather than print UI chrome; new reports persist '' instead (see
  // enrichContextualFindings).
  const scrubbed = findings.map((f) => {
    const ctx = stripAppOnboardingCopy(f.intentContext ?? '').trim();
    return ctx === (f.intentContext ?? '') ? f : { ...f, intentContext: ctx };
  });

  // KF-REDESIGN-1 — collapse the repeated INFLUENCES / WHY KEY / SOURCE
  // boilerplate to ONE compact line per finding (computed over the WHOLE list so
  // shared-verbatim strings are detected as boilerplate). Keyed by finding
  // reference so the grouped render below can look each up.
  const findingMeta = deriveFindingMeta(scrubbed);
  const metaByFinding = new Map<ContextualKeyFinding, FindingMeta>();
  scrubbed.forEach((f, i) => metaByFinding.set(f, findingMeta[i]!));

  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    items: scrubbed.filter(f => f.category === cat),
  })).filter(g => g.items.length > 0);

  // RRQ REPETITION FIX — the intent summary is stamped IDENTICALLY onto every
  // finding's `intentContext` (enrichContextualFindings stamps the one
  // per-report summary on each card), so the per-card "Intent context" box
  // reprinted the same verbose string N times. When every finding shares ONE
  // intentContext value, render it ONCE as a shared header above the groups
  // and SUPPRESS the per-card MetaRow. If findings carry DISTINCT intent
  // contexts (a future, finding-specific shape), the per-card row still
  // renders for each — no information loss.
  const distinctIntentContexts = Array.from(
    new Set(scrubbed.map(f => f.intentContext).filter(Boolean) as string[]),
  );
  const sharedIntentContext =
    distinctIntentContexts.length === 1 ? distinctIntentContexts[0] : null;

  // Type sizes track the active preset's headingScale instead of fixed
  // text-lg / text-[15px] / text-[11px] literals.
  const sizes = reportTypographySizes(theme ?? ({} as ReportTheme));

  return (
    <div
      className={`space-y-8 ${className}`}
      data-testid="contextual-key-findings"
      data-report-block="contextual-findings"
      {...(flowInColumn ? layoutGroupProps('row-group') : {})}
      // VISUAL-MATRIX FIX (editorial 2-col) — this block (intent ledger + Key
      // Findings) is a FULL-WIDTH exhibit, not flow prose. In the editorial
      // `--editorial` deck the host `projectIntentOverview` section is stamped
      // `data-section-flow="span"`, but this inner block was being fragmented into
      // the page's SECOND column (a sibling `<section>` paints at the right-column
      // x-offset), which the visual-matrix `intraCardOverflow` probe flags as a
      // 2-col crush — the lone executive-summary residual. Pin the block to a
      // SINGLE column and SPAN any enclosing multicol so its content can never be
      // split across / pushed into a 2nd column. `column-span:all` is inert when
      // no multicol ancestor exists (1-col presets / continuous viewer / exports
      // stay byte-identical), so this only PREVENTS the crush, never causes one.
      style={{
        // v1.2.1 #2 — pin the editorial body font so the finding titles + body
        // never inherit a leaked monospace cascade (the small-caps category
        // labels below keep their intentional MONO via an explicit override).
        fontFamily: theme?.bodyFont,
        maxWidth: '100%',
        // DEFECT-A — the `column-span:all` + `column-count:1` exhibit-pin is the
        // DEFAULT (keeps this block atomic/full-width in the editorial 2-col
        // deck). `flowInColumn` DROPS it so a single-column host section can
        // fragment the block across a page seam (see prop docs).
        ...(flowInColumn ? {} : { columnCount: 1, columnSpan: 'all' as const }),
      }}
    >
      {!omitHeading && (
        <h3
          className="font-semibold tracking-tight m-0"
          data-heading="true"
          style={{ color: roleColor(theme, 'ink'), fontSize: sizes.h3, fontFamily: theme?.displayFont || theme?.headingFont }}
        >
          Key Findings
        </h3>
      )}
      {/* RRQ REPETITION FIX — the shared "Intent context" box, rendered ONCE
          for the whole block (when every finding carries the same intent
          summary) instead of once per card. */}
      {sharedIntentContext && (
        <dl
          className="m-0"
          // STRIP CONTENT BOX (William) — no frame at all; the intent-context
          // note flows bare on cream (its small-caps MetaRow label is the only
          // structure), matching the datasheet sections.
          style={{ background: 'transparent' }}
          data-testid="shared-intent-context"
        >
          <MetaRow label="Intent context" theme={theme}>
            <IntentContextValue text={sharedIntentContext} theme={theme} intent={displayIntent} />
          </MetaRow>
        </dl>
      )}
      {grouped.map(({ category, items }) => (
        <section
          key={category}
          className="space-y-3.5"
          {...layoutGroupProps('row-group')}
          data-findings-group="true"
          data-testid={`contextual-findings-${category}`}
        >
          {/* Category head — small-caps mono label on a hairline rule
              (EditorialSubsection-style) instead of a bordered micro-heading.
              DEFECT 2 — `data-heading="true"` opts the category head into the
              EXISTING parity-locked keep-with-next rules (break-after:avoid +
              `[data-heading]+*` break-before:avoid) so a category head never
              strands at a page foot, split from its first finding. NO shared
              CSS change (density-parity stays GREEN). */}
          <h4
            className="text-[11px] font-semibold uppercase m-0 pb-1"
            data-heading="true"
            style={{
              fontFamily: MONO,
              letterSpacing: '0.14em',
              color: roleColor(theme, 'muted'),
              borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
            }}
          >
            {CATEGORY_LABELS[category]}
          </h4>
          <ul className="list-none space-y-6 pl-0 m-0" {...layoutGroupProps('row-group')}>
            {items.map((f, i) => {
              const impact = f.impact === 'positive' || f.impact === 'negative' ? f.impact : 'neutral';
              // RRQ REPETITION FIX — when the intent context is shared across
              // the block it is rendered ONCE in the header above; suppress the
              // per-card copy so it does not repeat N times.
              const showCardIntentContext = Boolean(f.intentContext) && !sharedIntentContext;
              // KF-REDESIGN-1 — the repeated INFLUENCES/WHY-KEY boilerplate is
              // collapsed away (William "not well structured"): only genuinely
              // finding-specific rows survive; the source remains as the single
              // compact provenance footer (suppressed when the ref is already
              // inline, per the pre-existing one-affordance rule).
              const meta = metaByFinding.get(f) ?? { metaLine: null, extraRows: [] };
              const showSource = Boolean(f.source) && !findingSourceIsRedundant(f);
              const hasMeta = Boolean(showCardIntentContext || meta.extraRows.length > 0 || f.relevanceGate);
              // Skip the detail paragraph when it is a verbatim echo of the
              // title (see normalizeFindingText) — a data condition that
              // otherwise prints the same sentence twice (heading + body).
              const showDetail =
                Boolean(f.detail) &&
                normalizeFindingText(f.detail) !== normalizeFindingText(f.title);

              return (
                <li
                  key={`${category}-${i}`}
                  {...layoutGroupProps('row')}
                  data-paged-findings-first-row={i === 0 ? 'true' : undefined}
                  // Editorial flow: a hairline TOP rule + a tinted left rule
                  // keyed off impact (positive → positive, negative → negative,
                  // neutral → structural rule). No card / shadow / surface wash.
                  style={{
                    // William QA 2026-06-28 — left accent rule removed; findings
                    // separate via the hairline TOP rule + spacing alone.
                    borderTop: `1px solid ${roleColor(theme, 'rule')}`,
                    // a finding card never splits mid-card across a page break.
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                  }}
                  data-testid="contextual-finding-card"
                >
                  <div className="pt-2 space-y-1.5">
                    <header className="space-y-1">
                      <h5
                        className="m-0 font-semibold leading-snug tracking-tight"
                        style={{ color: roleColor(theme, 'ink'), fontSize: sizes.h3 }}
                      >
                        {walkClaimsText(formatFindingTitle(f.title), report.references, report.claims)}
                      </h5>
                      {showDetail && (
                        <p
                          className="m-0 leading-[1.65]"
                          style={{ color: roleColor(theme, 'body'), fontSize: sizes.body }}
                        >
                          {walkClaimsText(f.detail, report.references, report.claims)}
                        </p>
                      )}
                    </header>

                    {hasMeta && (
                      <dl
                        // William QA 2026-06-28 — condensed (Key Findings must fit
                        // one page with the feasibility chart): tighter meta-row
                        // spacing, no frame; the rows flow bare under the heading.
                        className="m-0 space-y-1"
                        style={{ background: 'transparent' }}
                      >
                        {showCardIntentContext && f.intentContext && (
                          <MetaRow label="Intent context" theme={theme}>
                            <IntentContextValue text={f.intentContext} theme={theme} intent={displayIntent} />
                          </MetaRow>
                        )}
                        {meta.extraRows.map((r) => (
                          <MetaRow key={r.label} label={r.label} theme={theme}>{r.value}</MetaRow>
                        ))}
                        {f.relevanceGate && (
                          <div className="space-y-1.5 pt-0.5" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
                            <MetaRow label="Applies when" theme={theme}>{f.relevanceGate.appliesWhen}</MetaRow>
                            {f.relevanceGate.notApplicableWhen && (
                              <MetaRow label="Not when" theme={theme}>{f.relevanceGate.notApplicableWhen}</MetaRow>
                            )}
                          </div>
                        )}
                      </dl>
                    )}

                    {hasHaleuIntentConflict(report.projectIntentAtGen, `${f.title} ${f.detail}`) && (
                      // Wrapper preserves the asserted data-testid (the
                      // EditorialCallout props are typed and do not surface it).
                      <div data-testid="haleu-intent-conflict">
                        <EditorialCallout theme={theme ?? ({} as ReportTheme)} tone="warning">
                          HALEU finding may not apply to your selected reactor design class (LEU path). Update Project Intent → Reactor design class if this constraint is irrelevant.
                        </EditorialCallout>
                      </div>
                    )}

                    {showSource && (
                      <footer className="pt-1" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }} data-testid="contextual-finding-source">
                        <ReportSourceNote variant="label">
                          Source: {f.source}
                        </ReportSourceNote>
                      </footer>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
